/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFieldArray, useFormContext } from '@jalik/react-form'
import { capitalize } from 'lodash'
import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import FormButton from '../../../../client/js/components/form/buttons/FormButton'
import FormField from '../../../../client/js/components/form/fields/FormField'
import { PER_MONTH, PER_QUARTER, PER_YEAR } from '../../lib/period.mjs'
import { ActionIcon, Alert, Box, Collapse, Group, Select, Stack, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { faMinusSquare } from '@fortawesome/free-solid-svg-icons/faMinusSquare'
import { faPlusSquare } from '@fortawesome/free-solid-svg-icons/faPlusSquare'
import FormNumberField from '../../../../client/js/components/form/fields/FormNumberField'
import { parseBoolean } from '@jalik/form-parser'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle'

export type ChargeItemProps = {
  disabled?: boolean,
  index: number,
  item: Partial<EstateCharge>,
  name: string,
  onRemove (event: React.MouseEvent): void
}

export function ChargeItem (props: ChargeItemProps) {
  const {
    disabled,
    index,
    item,
    name,
    onRemove,
    ...others
  } = props

  const intl = useIntl()
  const [opened, { toggle }] = useDisclosure(item.id == null)

  const handleRemove = useCallback((event: React.MouseEvent) => {
    if (window.confirm(intl.formatMessage({
      id: 'ask_delete_charge',
      defaultMessage: 'Voulez-vous supprimer la charge "{label}" ?'
    }, { label: item.label }))) {
      onRemove(event)
    }
  }, [intl, item, onRemove])

  return (
    <Group align="start">
      <ActionIcon onClick={toggle}>
        <FontAwesomeIcon icon={opened ? faMinusSquare : faPlusSquare} />
      </ActionIcon>
      <Stack style={{ flexGrow: 1 }}>
        <Group align="end">
          <Box style={{ flexGrow: 1 }}>
            <FormField
              {...others}
              label={intl.formatMessage({
                id: 'label',
                defaultMessage: 'Libellé'
              })}
              name={`${name}[${index}].label`}
              parser={capitalize}
            />
          </Box>
          <FormField
            name={`${name}[${index}].provisioned`}
            label={intl.formatMessage({
              id: 'provisioned',
              defaultMessage: 'Provisionné'
            })}
            render={Select}
            parser={parseBoolean}
            leftSection={
              <Tooltip
                maw={200}
                label={intl.formatMessage({
                  id: 'provisioned_help',
                  defaultMessage: 'Provisionné = mois à venir, Non provisionné = mois passé'
                })}
                multiline
                withArrow
              >
                <FontAwesomeIcon fixedWidth icon={faQuestionCircle} />
              </Tooltip>
            }
            data={[
              {
                value: 'true',
                label: intl.formatMessage({
                  id: 'provisioned',
                  defaultMessage: 'Provisionné'
                })
              },
              {
                value: 'false',
                label: intl.formatMessage({
                  id: 'not_provisioned',
                  defaultMessage: 'Non provisionné'
                })
              }
            ]}
          />
          <FormField
            {...others}
            label={intl.formatMessage({
              id: 'frequency',
              defaultMessage: 'Fréquence'
            })}
            name={`${name}[${index}].frequency`}
            render={Select}
            data={[
              {
                value: PER_MONTH,
                label: intl.formatMessage({
                  id: 'monthly',
                  defaultMessage: 'Mensuel'
                })
              },
              {
                value: PER_QUARTER,
                label: intl.formatMessage({
                  id: 'quarterly',
                  defaultMessage: 'Trimestriel'
                })
              },
              {
                value: PER_YEAR,
                label: intl.formatMessage({
                  id: 'yearly',
                  defaultMessage: 'Annuel'
                })
              }
            ]}
          />
          <FormButton
            ms="auto"
            disabled={disabled}
            onClick={handleRemove}
            title={intl.formatMessage({
              id: 'delete',
              defaultMessage: 'Supprimer'
            })}
          >
            <FontAwesomeIcon
              icon={faTrash}
              fixedWidth
            />
          </FormButton>
        </Group>

        <Collapse in={opened}>
          <Group>
            <FormNumberField
              {...others}
              label={intl.formatMessage({
                id: 'amount_tax_included',
                defaultMessage: 'Montant TTC'
              })}
              min={0}
              name={`${name}[${index}].amount`}
            />
            <FormNumberField
              {...others}
              label={intl.formatMessage({
                id: 'including_vat',
                defaultMessage: 'Dont TVA'
              })}
              min={0}
              name={`${name}[${index}].vat`}
            />
            <FormNumberField
              {...others}
              label={intl.formatMessage({
                id: 'including_cps',
                defaultMessage: 'Dont CPS'
              })}
              min={0}
              name={`${name}[${index}].cps`}
            />
          </Group>
        </Collapse>
      </Stack>
    </Group>
  )
}

export type ChargesFieldProps = React.HTMLProps<HTMLElement> & {
  disabled?: boolean,
  name: string
}

function ChargesField (props: ChargesFieldProps) {
  const { disabled, name } = props
  const form = useFormContext()

  const { items, handleAppend, handleRemove } = useFieldArray({
    context: form,
    name,
    defaultValue: {}
  })

  return (
    <Stack>
      <Group>
        <FormButton
          disabled={disabled}
          onClick={handleAppend}
          variant="default"
        >
          <FormattedMessage
            id="add_periodic_charge"
            defaultMessage="Ajouter une charge périodique"
          />
        </FormButton>
      </Group>

      <Alert color="gray" icon={<FontAwesomeIcon fixedWidth icon={faQuestionCircle} />}>
        <FormattedMessage
          id="periodic_charge_help"
          defaultMessage="Les charges périodiques sont ajoutées automatiquement dans les factures de loyer selon une fréquence définie (mensuelle, annuelle...)."
        />
      </Alert>

      {items.map((field, index) => (
        <ChargeItem
          key={field.key}
          disabled={disabled}
          index={index}
          item={field.value}
          name={name}
          onRemove={handleRemove(index)}
        />
      ))}
    </Stack>
  )
}

export default ChargesField
